import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { DynamicForm } from 'dynamic-form/interfaces/dynamic-form-elements.interface';
import { parseAdditionalWidgetOptions } from 'dynamic-form/util/parse-additional-widget-options';
import { Observable, catchError, map, of } from 'rxjs';
import { RESTRICTIVE_UI_LISTINGS } from 'shared/constants/storage-keys';
import { OutletDistributionTypes, distributionCategoryEnumMap } from 'shared/enums/distribution-types.enum';
import { generateHttpParams } from 'shared/functions/generate-http-params.function';
import { BYPASS_ERROR_HANDLER_FOR_ERROR_CODES } from 'shared/interceptors/error.interceptor';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { CachingService, EventBusService, StorageService, ToastService } from 'shared/services';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { Outlet } from '../interfaces/outlet';

const LISTING_API = environment.BASE_URL + 'listingApi/';
const urls = {
    createOutlet: 'outlets/create?listingId=',
    duplicateOutlet: 'outlets/duplicate-outlet',
    deleteOutlet: 'outlets/',
    values: {
        base: 'values',
        updateField: 'values/updateField',
        updateTableField: 'values/updateDocument',
        createTableField: 'values/createDocument',
        deleteTableField: 'values/deleteDocument',
        outletData: 'values/current-outlet-data',
    },
    settings: {
        base: 'settings',
        createOutletStructure: 'settings/getCreateOutletFormDetails',
        channelRestrictedSettings: 'settings/channel-restricted-settings',
    },
};

@Injectable({
    providedIn: 'root',
})
export class OutletsService {
    private apollo: ApolloBase;

    constructor(
        private http: HttpClient,
        private apolloProvider: Apollo,
        private translate: TranslateService,
        private storageService: StorageService,
        private toastService: ToastService,
        private eventBusService: EventBusService,
        private featureFlagService: FeatureFlagService,
        private cachingService: CachingService,
    ) {
        this.apollo = this.apolloProvider.use('listingAPI');
    }

    public getOutletsOfListing(listingId: string): Observable<Outlet[]> {
        const query = `query searchOutlets {
                           outlets {
                              searchOutlets(
                                 searchQuery: { pageNumber: 1,
                                    pageSize: 100
                                 },
                                 filtering: { listingId: "${listingId}" }
                              ) {
                                 data {
                                    id
                                    outletTypeId
                                    name
                                    listingId
                                    isActive
                                 }
                                 totalCount
                                 statusCode
                                 message
                              }
                           }
                        }`;

        return this.apollo
            .watchQuery({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .valueChanges.pipe(map((response: any) => response.data.outlets.searchOutlets.data));
    }

    public getOutletsForListingOverview(listingId: string): Observable<{ outlets: Outlet[]; totalCount: number }> {
        const query = `query searchOutlets {
                           outlets {
                              searchOutlets(
                                 searchQuery: { pageNumber: 1,
                                    pageSize: 5
                                 },
                                 filtering: { listingId: "${listingId}" }
                              ) {
                                 data {
                                    id
                                    outletTypeId
                                    name
                                    listingId
                                    isActive
                                    outletStatus
                                 }
                                 totalCount
                                 statusCode
                                 message
                              }
                           }
                        }`;

        return this.apollo
            .watchQuery({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .valueChanges.pipe(
                map((response: any) => {
                    const data = response.data.outlets.searchOutlets.data;
                    const totalCount = response.data.outlets.searchOutlets.totalCount;
                    return { outlets: data, totalCount: totalCount };
                }),
            );
    }

    public getOutlet(listingId: string, outletId: string): Observable<Outlet> {
        const query = `query searchOutlets {
                           outlets {
                              searchOutlets(
                                 filtering: { listingId: "${listingId}",
                                    searchTerm: "${outletId}"
                                  }
                              ) {
                                 data {
                                    id
                                    outletTypeId
                                    name
                                    listingId
                                 }
                                 totalCount
                                 statusCode
                                 message
                              }
                           }
                        }`;

        return this.apollo
            .watchQuery({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .valueChanges.pipe(map((response: any) => response.data.outlets.searchOutlets.data[0]));
    }

    public getOutletForComparisonData(listingId: string, outletId: string): Observable<Outlet> {
        return this.http.get<Outlet>(LISTING_API + urls.values.outletData, {
            params: {
                listingId: listingId,
                outletId: outletId,
            },
        }) as Observable<Outlet>;
    }

    // Get outlet form structure from the back-end
    public getCreateOutletFormStructure(): Observable<DynamicForm> {
        return this.http.get(LISTING_API + urls.settings.createOutletStructure) as Observable<DynamicForm>;
    }

    public createOutlet(listingId: string, outletData: { [key: string]: any }): Observable<any> {
        return this.http.post<Outlet>(LISTING_API + urls.createOutlet + listingId, outletData);
    }

    /**
     * Get the form structure (dynamic form) for any outlet and any feature group for that outlet
     * @param outletId
     * @param featureGroupId
     * @param listingId
     */
    public getFormStructure(outletId: string, featureGroupId: string, listingId: string): Observable<DynamicForm> {
        const restrictiveUIForListing: { [key: string]: string } = JSON.parse(
            (this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string) || '{}',
        );
        if (restrictiveUIForListing?.[listingId] && this.featureFlagService.featureFlags()[FeatureFlagEnum.RESTRICTIVE_UI]) {
            return this.getChannelRestrictiveFormStructure(outletId, featureGroupId, listingId, restrictiveUIForListing[listingId]);
        } else {
            return this.cachingService.wrapCache(
                'getFormStructure',
                (outletIdParam: string, featureGroupIdParam: string) => {
                    return (
                        this.http.get(LISTING_API + urls.settings.base, {
                            params: {
                                outletId: outletIdParam,
                                featureGroupId: featureGroupIdParam,
                            },
                        }) as Observable<DynamicForm>
                    ).pipe(
                        map((dynamicForm: DynamicForm) => {
                            parseAdditionalWidgetOptions(dynamicForm.inputs);
                            return dynamicForm;
                        }),
                    );
                },
                [outletId, featureGroupId],
                { ttl: 1000 },
            );
        }
    }

    /**
     * Based on the outlet's listing's selected restrictive UI channel,
     * get the form structure (dynamic form) for said outlet and any feature group for that listing.
     * @param listingId
     * @param outletId
     * @param featureGroupId
     *
     * @param channelId - The channel id set on the listing's restrictive UI channel
     */
    public getChannelRestrictiveFormStructure(
        outletId: string,
        featureGroupId: string,
        listingId: string,
        channelId: string,
    ): Observable<DynamicForm> {
        return (
            this.http.get(LISTING_API + urls.settings.channelRestrictedSettings, {
                params: {
                    outletId: outletId,
                    listingId: listingId,
                    featureGroupId: featureGroupId,
                    channelId: channelId,
                },
                context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [409]),
            }) as Observable<any> as Observable<DynamicForm>
        ).pipe(
            map((dynamicForm: DynamicForm) => {
                parseAdditionalWidgetOptions(dynamicForm.inputs);
                return dynamicForm;
            }),
            catchError((err) => {
                // This error is returned when the channel saved by the user in local storage is no longer
                // available to be chosen from the outlet's/listing's restrictive UI channels list.
                // Because if this, we need to remove it from the local storage as well,
                // as it can't be used to restrict the UI anymore
                if (err.status === 409) {
                    const message = this.translate.instant('listings.userPreferences.restrictiveUIChannelNotAvailable');
                    this.toastService.showToast(message, 'error');

                    const currentRestrictiveUIPreferencesFromStorageParsed = JSON.parse(
                        (this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string) || '{}',
                    );

                    if (currentRestrictiveUIPreferencesFromStorageParsed[listingId]) {
                        delete currentRestrictiveUIPreferencesFromStorageParsed[listingId];
                        this.storageService.setInLocal(
                            RESTRICTIVE_UI_LISTINGS,
                            JSON.stringify(currentRestrictiveUIPreferencesFromStorageParsed),
                        );
                        this.eventBusService.dispatch('listing:userPreferences:restrictiveUiChannelsUpdate', true);
                        return this.getFormStructure(outletId, featureGroupId, listingId);
                    }
                }
                return of(err);
            }),
        );
    }

    /**
     * Get the form values for any outlet and any feature group for that outlet
     * @param outletId
     * @param featureGroupId
     */
    public getFormValues(outletId: string, featureGroupId: string): Observable<any> {
        return this.cachingService.wrapCache(
            'getFormValues',
            (outletIdParam: string, featureGroupIdParam: string) => {
                return this.http.get(LISTING_API + urls.values.base, {
                    params: {
                        featureGroupId: featureGroupIdParam,
                        outletId: outletIdParam,
                    },
                });
            },
            [outletId, featureGroupId],
            { ttl: 1000 },
        );
    }

    public createTableValues(outletId: string, changedData: any): Observable<any> {
        return this.http.post(`${LISTING_API + urls.values.createTableField}`, changedData, {
            params: {
                outletId: outletId,
            },
        }) as Observable<any>;
    }

    public updateTableValues(outletId: string, changedData: any, listingId: string): Observable<any> {
        const paramsObject: { params: { outletId: string; updateFromRestrictedView?: boolean } } = {
            params: {
                outletId: outletId,
            },
        };
        const restrictiveUIForListing: { [key: string]: string } = JSON.parse(
            (this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string) || '{}',
        );
        if (restrictiveUIForListing?.[listingId] && this.featureFlagService.featureFlags()[FeatureFlagEnum.RESTRICTIVE_UI]) {
            paramsObject.params.updateFromRestrictedView = true;
        }

        return this.http.put(`${LISTING_API + urls.values.updateTableField}`, changedData, paramsObject) as Observable<any>;
    }

    public deleteTableValues(outletId: string, changedData: any): Observable<any> {
        return this.http.delete(`${LISTING_API + urls.values.deleteTableField}`, {
            body: changedData,
            params: {
                outletId: outletId,
            },
        }) as Observable<any>;
    }

    public updateValues(outletId: string, changedData: any): Observable<any> {
        return this.http.post(`${LISTING_API + urls.values.updateField}`, changedData, {
            params: {
                outletId: outletId,
            },
        }) as Observable<any>;
    }

    public deleteOutlet(listingId: string, outletId: string): Observable<any> {
        return this.http.delete(`${LISTING_API + urls.deleteOutlet + listingId}/${outletId}`) as Observable<any>;
    }

    public duplicateOutlet(outletId: string, outletName: string): Observable<any> {
        return this.http.post<Outlet>(LISTING_API + urls.duplicateOutlet, null, {
            params: {
                outletId: outletId,
                outletName: outletName,
            },
        });
    }

    public getChannels(listingId: string, featureGroupId: string): Observable<any> {
        return this.cachingService.wrapCache(
            'getChannels',
            (listingIdParam: string, featureGroupIdParam: string) => {
                const params = generateHttpParams({ listingId: listingIdParam, featureGroupId: featureGroupIdParam });
                return this.http.get(LISTING_API + 'values/get-listing-channels', { params });
            },
            [listingId, featureGroupId],
            { ttl: 1000 },
        );
    }

    public getOutletsDistributionSettings(listingId: string, outletId: string, outletFeatureGroupId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query getOutletDistribution {
                    outlets {
                        getOutletDistribution (
                            outletId: "${outletId}",
                            featureGroupId: "${outletFeatureGroupId}"
                            listingId: "${listingId}"
                        ) {
                            data {
                                distributionSettings {
                                    featureGroupId,
                                    arrayItemId,
                                    listingId,
                                    arrayItemDistributionCategory,
                                    distributionRestrictions {
                                        channelId
                                    }
                                }
                            },
                            message,
                            statusCode
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => response.data.outlets.getOutletDistribution.data[0].distributionSettings),
                map((data) => {
                    const distributionTypesBEMap: { [key: string]: OutletDistributionTypes } = {
                        BACK_TO_LISTING_DISTRIBUTION: OutletDistributionTypes.BACK_TO_LISTING_DISTRIBUTION,
                        BACK_TO_BRAND_DISTRIBUTION: OutletDistributionTypes.BACK_TO_BRAND_DISTRIBUTION,
                        INCLUDE_ONLY_SELECTED: OutletDistributionTypes.INCLUDE_ONLY_SELECTED,
                        INCLUDE_ALL_EXCEPT_SELECTED: OutletDistributionTypes.INCLUDE_ALL_EXCEPT_SELECTED,
                    };

                    return {
                        distributionType: distributionTypesBEMap[data.arrayItemDistributionCategory],
                        selectedChannels: data.distributionRestrictions?.map((restriction: any) => restriction.channelId),
                    };
                }),
            );
    }

    public saveDistributionSettings(
        outletId: string,
        featureGroupId: string,
        listingId: string,
        distributionSettings: any,
    ): Observable<any> {
        const distributionType =
            distributionSettings.distributionType.toString() === OutletDistributionTypes.BACK_TO_LISTING_DISTRIBUTION.toString()
                ? 'BACK_TO_LISTING_DISTRIBUTION'
                : distributionCategoryEnumMap[distributionSettings.distributionType];
        let outletMappings = '';

        if (
            [
                OutletDistributionTypes.INCLUDE_ONLY_SELECTED.toString(),
                OutletDistributionTypes.INCLUDE_ALL_EXCEPT_SELECTED.toString(),
            ].includes(distributionSettings.distributionType.toString())
        ) {
            outletMappings = distributionSettings.selectedChannels
                .map((channelId: string) => {
                    return `{ channelId: "${channelId}" }`;
                })
                .join(', ');

            outletMappings = `outletDistributionRestrictions: [${outletMappings}]`;
        }

        return this.apollo
            .mutate({
                mutation: gql`
                mutation setListingOutletDistribution {
                    outlets {
                        setListingOutletDistribution (
                            outletId: "${outletId}",
                            outletDistributionSettings: {
                                featureGroupId: "${featureGroupId}",
                                arrayItemId: "${outletId}",
                                listingId: "${listingId}",
                                arrayItemDistributionCategory: ${distributionType},
                            },
                            ${outletMappings}
                        ) {
                            data,
                            message,
                            statusCode
                        }
                    }
                }
            `,
                variables: {},
            })
            .pipe(map((response: any) => response.data.outlets.getOutletsDistribution));
    }
}
